import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";

export default function PayRun() {
  const [settings, setSettings] = useState(null);
  // These two are INDEPENDENT -- not complements. The box editors do NOT sync
  // them. Back office = expense the back office charges (default 35%).
  // Driver's share = what the driver keeps of what's LEFT after back office
  // (default 50%). Example: $305 line haul, BO=35, Driver=50 -> driver gets
  // $305 * 0.65 * 0.50 = $99.13 + 100% fuel surcharge.
  const [driverPct, setDriverPct] = useState("");
  const [backOfficePct, setBackOfficePct] = useState("");
  const [savedDriverPct, setSavedDriverPct] = useState("");
  const [savedBoPct, setSavedBoPct] = useState("");
  // Team-side defaults -- STRICTLY independent of the single-driver fields.
  // Edits to team boxes never touch single-driver settings and vice versa.
  const [teamDriverPct, setTeamDriverPct] = useState("");
  const [teamBoPct, setTeamBoPct] = useState("");
  const [teamPayPerMile, setTeamPayPerMile] = useState("");
  const [applying, setApplying] = useState(false);
  const [form, setForm] = useState({
    pay_period_start: "",
    pay_period_end: "",
    pay_date: "",
    default_pay_per_mile: "",
    default_pay_period: "weekly",
    // Day of week the pay week starts on. 0=Sun .. 6=Sat. Default 4=Thursday
    // (so Thursday → Wednesday). Buyer-configurable from the Pay Cycle card.
    week_start_dow: 4,
    // Days between period end and pay date. 2 => Fri pay for a Thu-Wed cycle.
    pay_date_offset_days: 2,
    auto_advance_weekly: true,
    overtime_rule: "",
    guarantee_rule: "",
    deduction_categories: "Fuel advance, Cash advance, Insurance, Escrow, Chargeback, Loan repayment, Other deduction",
    reimbursement_categories: "Fuel, Tolls, Lumper, Detention, Per diem, Other reimbursement",
  });

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (r.data.pay_run) setForm((f) => ({ ...f, ...r.data.pay_run }));
      const dp = Number(r.data?.default_driver_pct);
      const bp = Number(r.data?.default_back_office_pct);
      if (!Number.isNaN(dp) && dp > 0 && r.data?.default_driver_pct != null) {
        setSavedDriverPct(dp); setDriverPct(String(dp));
      }
      if (!Number.isNaN(bp) && bp >= 0 && r.data?.default_back_office_pct != null) {
        setSavedBoPct(bp); setBackOfficePct(String(bp));
      }
      // Team-side fields -- never fall back to the single-side values.
      if (r.data?.default_team_driver_pct != null) setTeamDriverPct(String(r.data.default_team_driver_pct));
      if (r.data?.default_team_back_office_pct != null) setTeamBoPct(String(r.data.default_team_back_office_pct));
      if (r.data?.pay_run?.default_team_pay_per_mile != null) setTeamPayPerMile(String(r.data.pay_run.default_team_pay_per_mile));
    }).catch(() => {});
  }, []);

  // Save team-side defaults to settings only. Does NOT touch driver records
  // or the single-driver defaults. Settings takes effect at calc time.
  const commitTeamSetting = async (key, raw, { min = 0, max = 1000000 } = {}) => {
    const v = parseFloat(raw);
    if (Number.isNaN(v) || v < min || v >= max) return;
    try {
      if (key === "default_team_pay_per_mile") {
        const cleanForm = { ...form,
          default_pay_per_mile: form.default_pay_per_mile === "" ? 0 : (parseFloat(form.default_pay_per_mile) || 0),
          default_team_pay_per_mile: v,
        };
        await api.put("/settings", { ...settings, pay_run: cleanForm });
      } else {
        await api.put("/settings", { ...settings, [key]: v });
      }
    } catch { toast.error("Save failed"); }
  };

  // Pushes a change to the company default + every percentage driver. Only
  // sends whichever key(s) are provided -- the two values are INDEPENDENT.
  const applyToAll = async ({ driver_pct, back_office_pct, silent = false } = {}) => {
    const payload = {};
    if (driver_pct != null && !Number.isNaN(driver_pct) && driver_pct > 0 && driver_pct < 100) {
      payload.driver_pct = driver_pct;
    }
    if (back_office_pct != null && !Number.isNaN(back_office_pct) && back_office_pct >= 0 && back_office_pct < 100) {
      payload.back_office_pct = back_office_pct;
    }
    if (!Object.keys(payload).length) {
      if (!silent) toast.error("Enter a value between 0 and 99");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post("/settings/apply-default-driver-pct", payload);
      if (payload.driver_pct != null) setSavedDriverPct(payload.driver_pct);
      if (payload.back_office_pct != null) setSavedBoPct(payload.back_office_pct);
      const parts = [];
      if (payload.driver_pct != null) parts.push(`Driver's share ${payload.driver_pct}%`);
      if (payload.back_office_pct != null) parts.push(`Back Office ${payload.back_office_pct}%`);
      toast.success(`${parts.join(" · ")} applied to ${r.data.updated_drivers} driver(s)`);
    } catch (e) {
      toast.error("Failed to apply");
    } finally {
      setApplying(false);
    }
  };

  // Auto-apply on blur -- only touches the value that actually changed.
  const commitDriver = (raw) => {
    const v = parseFloat(raw);
    if (Number.isNaN(v) || v <= 0 || v >= 100) return;
    if (Math.abs(v - savedDriverPct) < 0.0001) return;
    applyToAll({ driver_pct: v, silent: true });
  };
  const commitBo = (raw) => {
    const v = parseFloat(raw);
    if (Number.isNaN(v) || v < 0 || v >= 100) return;
    if (Math.abs(v - savedBoPct) < 0.0001) return;
    applyToAll({ back_office_pct: v, silent: true });
  };

  const save = async () => {
    try {
      // Normalize blank numeric fields so Pydantic won't choke on "" strings.
      const cleanForm = { ...form,
        default_pay_per_mile: form.default_pay_per_mile === "" ? 0 : (parseFloat(form.default_pay_per_mile) || 0),
      };
      await api.put("/settings", { ...settings, pay_run: cleanForm });
      const payload = {};
      if (driverPct !== "") payload.driver_pct = parseFloat(driverPct);
      if (backOfficePct !== "") payload.back_office_pct = parseFloat(backOfficePct);
      if (Object.keys(payload).length) await applyToAll({ ...payload, silent: true });
      toast.success("Pay run defaults saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const setCurrentWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const startDow = Number(form.week_start_dow ?? 4);
    const offset = Number(form.pay_date_offset_days ?? 2);
    // Walk back to the most recent occurrence of the chosen start day.
    const start = new Date(now); start.setDate(now.getDate() - ((day - startDow + 7) % 7));
    const end = new Date(start); end.setDate(start.getDate() + 6);
    const payDate = new Date(end); payDate.setDate(end.getDate() + offset);
    const iso = (d) => d.toISOString().slice(0, 10);
    setForm((f) => ({ ...f, pay_period_start: iso(start), pay_period_end: iso(end), pay_date: iso(payDate) }));
  };

  const advanceWeek = () => {
    const addDays = (iso, days) => {
      const d = new Date(iso); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10);
    };
    setForm((f) => ({
      ...f,
      pay_period_start: f.pay_period_start ? addDays(f.pay_period_start, 7) : "",
      pay_period_end: f.pay_period_end ? addDays(f.pay_period_end, 7) : "",
      pay_date: f.pay_date ? addDays(f.pay_date, 7) : "",
    }));
  };

  const DOWS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const startDowName = DOWS[Number(form.week_start_dow ?? 4)];
  const endDowName = DOWS[(Number(form.week_start_dow ?? 4) + 6) % 7];
  const payDowName = DOWS[((Number(form.week_start_dow ?? 4) + 6) + Number(form.pay_date_offset_days ?? 2)) % 7];

  if (!settings) return <div className="text-slate-500">Loading...</div>;

  // Live preview of the math: 100mi × $3.05 base × fsr $0.90/mi to show paper match.
  const dp = parseFloat(driverPct) || 0;
  const bp = parseFloat(backOfficePct) || 0;
  const miles = 100;
  const baseRate = 3.05;
  const fsr = 0.90;
  const lineHaul = miles * baseRate;                // 305
  const afterBo = lineHaul * (1 - bp / 100);        // 198.25 (bp=35)
  const fuel = miles * fsr;                         // 90
  const subtotal = afterBo + fuel;                  // 288.25
  const driverCut = subtotal * (dp / 100);          // 144.125 (dp=50)

  return (
    <div className="space-y-6" data-testid="pay-run-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Pay Run</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Payroll defaults kept in your company settings — Pay Run is the single source of truth for percentage splits.
          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} data-testid="save-pay-run-btn">Save payroll defaults</Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">Current Pay Run</div>
            <div className="text-xs text-slate-500 mt-0.5">The period and pay date this run uses — applied to trip loading, statements and payroll export.</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={setCurrentWeek} data-testid="set-current-week">Set to current run</Button>
            <Button size="sm" variant="outline" onClick={advanceWeek} data-testid="advance-week">Advance to following week</Button>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Switch checked={form.auto_advance_weekly} onCheckedChange={(v) => setForm({ ...form, auto_advance_weekly: v })} data-testid="auto-advance-toggle" />
          <div>
            <div className="text-sm font-semibold">Auto-advance weekly</div>
            <div className="text-xs text-slate-500">Rolls the run forward 7 days and keeps the same pay-date offset from the Pay Cycle card above.</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Pay Period Start</Label><Input type="date" value={form.pay_period_start} onChange={(e) => setForm({ ...form, pay_period_start: e.target.value })} data-testid="pay-period-start" /></div>
          <div><Label>Pay Period End</Label><Input type="date" value={form.pay_period_end} onChange={(e) => setForm({ ...form, pay_period_end: e.target.value })} data-testid="pay-period-end" /></div>
          <div><Label>Pay Date</Label><Input type="date" value={form.pay_date} onChange={(e) => setForm({ ...form, pay_date: e.target.value })} data-testid="pay-date-default" /></div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="pay-cycle-card">
        <div className="eyebrow">Pay Cycle</div>
        <div className="text-xs text-slate-500 mt-0.5">Pick which day your pay week starts on and how many days after the period ends you pay drivers. "Set to current run" uses these settings.</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Pay week starts on</Label>
            <Select value={String(form.week_start_dow ?? 4)} onValueChange={(v) => setForm({ ...form, week_start_dow: Number(v) })}>
              <SelectTrigger data-testid="week-start-dow"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOWS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pay day = period end + ...</Label>
            <Select value={String(form.pay_date_offset_days ?? 2)} onValueChange={(v) => setForm({ ...form, pay_date_offset_days: Number(v) })}>
              <SelectTrigger data-testid="pay-offset-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0,1,2,3,4,5,6,7,10,14].map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? "Same day" : `+${n} day${n === 1 ? "" : "s"}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
            <div className="font-semibold text-slate-900">Preview</div>
            <div>Period: <span className="font-mono">{startDowName} → {endDowName}</span></div>
            <div>Pay date: <span className="font-mono">{payDowName}</span> (period end + {form.pay_date_offset_days} day{Number(form.pay_date_offset_days) === 1 ? "" : "s"})</div>
          </div>
        </div>
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pr-defaults-grid">
        {/* Single · Percentage */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="pr-single-pct-card">
          <div className="eyebrow">Single · Percentage</div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-700">Back Office %</Label>
              <div className="relative">
                <Input
                  type="number" step="0.1" min="0" max="100"
                  className="text-right font-mono h-11 pr-8"
                  value={backOfficePct}
                  onChange={(e) => setBackOfficePct(e.target.value)}
                  onBlur={(e) => commitBo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  data-testid="pr-back-office-pct"
                  placeholder=""
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-700">Driver's %</Label>
              <div className="relative">
                <Input
                  type="number" step="0.1" min="0" max="100"
                  className="text-right font-mono h-11 pr-8"
                  value={driverPct}
                  onChange={(e) => setDriverPct(e.target.value)}
                  onBlur={(e) => commitDriver(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  data-testid="pr-driver-pct"
                  placeholder=""
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Team · Percentage */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="pr-team-pct-card">
          <div className="eyebrow">Team · Percentage</div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-700">Back Office %</Label>
              <div className="relative">
                <Input
                  type="number" step="0.1" min="0" max="100"
                  className="text-right font-mono h-11 pr-8"
                  value={teamBoPct}
                  onChange={(e) => setTeamBoPct(e.target.value)}
                  onBlur={(e) => commitTeamSetting("default_team_back_office_pct", e.target.value, { max: 100 })}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  data-testid="pr-team-back-office-pct"
                  placeholder=""
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-700">Driver's %</Label>
              <div className="relative">
                <Input
                  type="number" step="0.1" min="0" max="100"
                  className="text-right font-mono h-11 pr-8"
                  value={teamDriverPct}
                  onChange={(e) => setTeamDriverPct(e.target.value)}
                  onBlur={(e) => commitTeamSetting("default_team_driver_pct", e.target.value, { max: 100 })}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  data-testid="pr-team-driver-pct"
                  placeholder=""
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
            </div>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">Each team driver earns half the full load pay.</div>
        </div>

        {/* Single · Per Mile */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="pr-single-mile-card">
          <div className="eyebrow">Single · Per Mile</div>
          <div className="mt-3">
            <Label className="text-xs text-slate-700">Pay Per Mile</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <Input
                type="number" step="0.01" min="0"
                className="text-right font-mono h-11 pl-7"
                value={form.default_pay_per_mile}
                onChange={(e) => setForm({ ...form, default_pay_per_mile: e.target.value })}
                onBlur={save}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                data-testid="pr-single-pay-per-mile"
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Team · Per Mile */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="pr-team-mile-card">
          <div className="eyebrow">Team · Per Mile</div>
          <div className="mt-3">
            <Label className="text-xs text-slate-700">Pay Per Mile</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <Input
                type="number" step="0.01" min="0"
                className="text-right font-mono h-11 pl-7"
                value={teamPayPerMile}
                onChange={(e) => setTeamPayPerMile(e.target.value)}
                onBlur={(e) => commitTeamSetting("default_team_pay_per_mile", e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                data-testid="pr-team-pay-per-mile"
                placeholder=""
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Each team driver earns this rate on the full miles.</div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="eyebrow">Payroll Defaults</div>
        <div className="text-xs text-slate-500 mt-0.5">Applied when a driver has no rate of their own, and used by the pay calculator and reports.</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Default Pay Period</Label>
            <Select value={form.default_pay_period} onValueChange={(v) => setForm({ ...form, default_pay_period: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">{`Weekly (${startDowName}–${endDowName})`}</SelectItem>
                <SelectItem value="biweekly">Bi-weekly</SelectItem>
                <SelectItem value="semimonthly">Semi-monthly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Overtime Rule</Label><Input value={form.overtime_rule} onChange={(e) => setForm({ ...form, overtime_rule: e.target.value })} placeholder="e.g. none for per-mile; OT after 55 hrs for hourly" /></div>
          <div><Label>Guarantee / Minimum Rule</Label><Input value={form.guarantee_rule} onChange={(e) => setForm({ ...form, guarantee_rule: e.target.value })} placeholder="e.g. $900 weekly minimum" /></div>
        </div>
        <div className="mt-4">
          <Label>Deduction Categories</Label>
          <Input value={form.deduction_categories} onChange={(e) => setForm({ ...form, deduction_categories: e.target.value })} />
          <div className="text-[11px] text-slate-500 mt-1">Comma separated — offered as suggestions on deduction lines.</div>
        </div>
        <div className="mt-3">
          <Label>Reimbursement Categories</Label>
          <Input value={form.reimbursement_categories} onChange={(e) => setForm({ ...form, reimbursement_categories: e.target.value })} />
          <div className="text-[11px] text-slate-500 mt-1">Comma separated — offered as suggestions on reimbursement lines.</div>
        </div>
      </div>
    </div>
  );
}
